import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  input,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { ACLService } from '@delon/acl';
import { _HttpClient } from '@delon/theme';
import { copy } from '@delon/util';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { ReportingService } from 'src/app/core/util/reporting.service';
import { InputModule } from 'src/app/input/input.module';
import { environment } from 'src/environments/environment';

import { TransactionPreviewBillComponent } from './transaction-preview/transaction-preview-bill.component';
import { TransactionPreviewCreditNoteComponent } from './transaction-preview/transaction-preview-credit-note.component';
import { TransactionPreviewCustomerRefundComponent } from './transaction-preview/transaction-preview-customer-refund.component';
import { TransactionPreviewDebitNoteComponent } from './transaction-preview/transaction-preview-debit-note.component';
import { TransactionPreviewPayBillComponent } from './transaction-preview/transaction-preview-pay-bill.component';
import { TransactionPreviewReceivePaymentComponent } from './transaction-preview/transaction-preview-receive-payment.component';

import { finalize } from 'rxjs';

@Component({
  selector: 'app-transaction-sender',
  templateUrl: './transaction-sender.component.html',
  styleUrls: ['./transaction-sender.component.less'],
  standalone: true,
  imports: [
    CommonModule,
    NzInputModule,
    NzSelectModule,
    NzModalModule,
    NzButtonModule,
    FormsModule,
    NzTableModule,
    NzGridModule,
    NzFormModule,
    ReactiveFormsModule,
    InputModule,
    NzAutocompleteModule,
    TransactionPreviewDebitNoteComponent,
    TransactionPreviewCreditNoteComponent,
    TransactionPreviewBillComponent,
    TransactionPreviewReceivePaymentComponent,
    TransactionPreviewPayBillComponent,
    TransactionPreviewCustomerRefundComponent
  ]
})
export class TransactionSenderComponent implements OnChanges, OnInit {
  private readonly defaultCompanyLogo =
    'https://qnestorage.blob.core.windows.net/cloud-accounting/Email%20Resouces/official-logo-new-colored.png';
  /** Native element so footer actions can call `requestSubmit()` (footer lives outside `<form>`). */
  @ViewChild('emailFormElement') emailFormElement?: ElementRef<HTMLFormElement>;
  @ViewChild('retryLink') linkModel!: any;

  readonly emailFormGroup: FormGroup;
  private emailFormSubmitted = false;

  // For Transaction Sender
  @Input() reportTypes: any[] = [];
  @Input() einvoiceControl: number = -1;
  // For Report Sender
  @Input() taskId: string | null = null;

  docType = input<string | null>(null);

  @Input() showPreview: boolean = false;
  @Output() readonly modalClosed = new EventEmitter<string>();
  /** Report preview share flow (`handleReport` / `GenerateLink`) — for toolbar spinner and disabling duplicate share clicks. */
  @Output() readonly reportShareLoadingChange = new EventEmitter<boolean>();

  modalVisible: any = {
    email: false,
    whatsapp: false,
    telegram: false
  };
  cachedLink: any = {
    data: null,
    messageId: null
  };
  loading: boolean = false;

  sendOption: string | null = null;

  /** Emits only when `loading` value changes (report share UX). */
  private setReportShareLoading(value: boolean): void {
    if (this.loading === value) {
      return;
    }
    this.loading = value;
    this.reportShareLoadingChange.emit(value);
    this.cdr.markForCheck();
  }
  data: any = {
    recipientBillTargetLabel: '',
    receipientEmail: '',
    cc: '',
    bcc: '',
    bccList: [],
    primaryEmail: '',
    ccList: [],
    ccOptions: []
  };
  readonly templateKeys = {
    defaultInvoice: 0,
    receivePayment: 1,
    debitNote: 2,
    creditNote: 3,
    bill: 4,
    customerRefund: 5,
    apPayBills: 6
  } as const;

  get previewTemplateKey(): number {
    return this.data?.templateKey ?? this.templateKeys.defaultInvoice;
  }

  filteredEmailSuggestions: string[] = [];
  response: any = null;
  errors: any = {};
  reportFormats: any[] = [];
  selectedReport: any = null;
  companyLogo: string = '';
  isBccVisible: boolean = false;

  get emailPreview() {
    const message = this.modalVisible.email
      ? String(this.emailFormGroup.get('message')?.value ?? '')
      : String(this.data.message ?? '');
    if (!message) return null;

    const text = message
      .replaceAll('\n\n', '<div><br></div>')
      .replaceAll('\n', '<div></div>');
    return text;
  }

  constructor(
    private fb: FormBuilder,
    private http: _HttpClient,
    private acl: ACLService,
    private msg: NzMessageService,
    private notificationSrv: NzNotificationService,
    private reporting: ReportingService,
    private cdr: ChangeDetectorRef
  ) {
    this.emailFormGroup = this.fb.group({
      receipientName: [''],
      receipientEmail: ['', Validators.required],
      ccList: [[] as string[]],
      bccList: [[] as string[]],
      subject: ['', Validators.required],
      message: ['']
    });
    this.emailFormGroup.get('receipientEmail')!.valueChanges.subscribe(v =>
      this.updateFilteredEmailSuggestions(typeof v === 'string' ? v : '')
    );
    this.emailFormGroup.get('message')!.valueChanges.subscribe(() => {
      if (this.modalVisible.email) {
        this.applyMessageLinkValidationFromEmailForm();
      }
    });
  }

  toggleBccField() {
    this.isBccVisible = !this.isBccVisible;
  }

  /** After the modal body is in the DOM, patch reactive form from `data` and clear touched state. */
  onEmailModalAfterOpen(): void {
    this.patchEmailFormFromData();
  }

  private patchEmailFormFromData(): void {
    this.emailFormSubmitted = false;
    this.emailFormGroup.patchValue(
      {
        receipientName: this.data.receipientName ?? '',
        receipientEmail: this.data.receipientEmail ?? '',
        ccList: [...(this.data.ccList ?? [])],
        bccList: [...(this.data.bccList ?? [])],
        subject: this.data.subject ?? '',
        message: this.data.message ?? ''
      },
      { emitEvent: false }
    );
    Object.values(this.emailFormGroup.controls).forEach(c => {
      c.markAsPristine();
      c.markAsUntouched();
    });
    this.applyMessageLinkValidationFromEmailForm();
    this.updateFilteredEmailSuggestions(this.data.receipientEmail ?? '');
    this.cdr.markForCheck();
  }

  private syncEmailDataFromForm(): void {
    const v = this.emailFormGroup.getRawValue() as {
      receipientName: string;
      receipientEmail: string;
      ccList: string[];
      bccList: string[];
      subject: string;
      message: string;
    };
    this.data.receipientName = v.receipientName;
    this.data.receipientEmail = v.receipientEmail;
    this.data.ccList = v.ccList ?? [];
    this.data.bccList = v.bccList ?? [];
    this.data.subject = v.subject;
    this.data.message = v.message;
  }

  private applyMessageLinkValidationFromEmailForm(): void {
    const text = String(this.emailFormGroup.get('message')?.value ?? '');
    this.errors.message = !text.includes('[link]');
  }

  /** Required-field hints: show after touch/dirty or after a submit attempt. */
  emailFieldShowsError(controlName: string, errorCode: string): boolean {
    const control = this.emailFormGroup.get(controlName);
    if (!control?.errors?.[errorCode]) {
      return false;
    }
    return !!(control.touched || control.dirty || this.emailFormSubmitted);
  }

  onEmailFormSubmit(ev: Event): void {
    ev.preventDefault();
    this.handleEmailSubmission();
  }

  submitEmailFormFromFooter(): void {
    const el = this.emailFormElement?.nativeElement;
    if (el) {
      el.requestSubmit();
    } else {
      this.handleEmailSubmission();
    }
  }

  validateEmails(emails: string): boolean {
    if (!emails) return true; // If field is empty, consider it valid (except for Send To)
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(emails.trim());
  }

  validateEmailArray(emails: string[]): boolean {
    if (!emails || emails.length === 0) return true; // Empty array is valid
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emails.every(email => emailPattern.test(email.trim()));
  }

  hasInvalidEmails(fieldName: 'receipientEmail' | 'ccList' | 'bccList'): boolean {
    if (fieldName === 'ccList') {
      return !this.validateEmailArray(
        this.emailFormGroup.get('ccList')?.value ?? []
      );
    }
    if (fieldName === 'bccList') {
      return !this.validateEmailArray(
        this.emailFormGroup.get('bccList')?.value ?? []
      );
    }
    const emails = this.emailFormGroup.get('receipientEmail')?.value;
    return emails ? !this.validateEmails(String(emails)) : false;
  }

  shouldShowPrimaryEmail(): boolean {
    if (!this.data.primaryEmail) return false;

    const currentInput = String(
      this.emailFormGroup.get('receipientEmail')?.value || ''
    );
    if (!currentInput) return true;

    return this.data.primaryEmail
      .toLowerCase()
      .includes(currentInput.toLowerCase());
  }

  private updateFilteredEmailSuggestions(inputValue: string) {
    if (!this.data.ccOptions) {
      this.filteredEmailSuggestions = [];
      return;
    }

    if (!inputValue) {
      this.filteredEmailSuggestions = [...this.data.ccOptions];
      return;
    }

    this.filteredEmailSuggestions = this.data.ccOptions.filter(
      (email: string) => email.toLowerCase().includes(inputValue.toLowerCase())
    );
  }

  isEmailDisabled(email: string, fieldName: string): boolean {
    const to = String(this.emailFormGroup.get('receipientEmail')?.value ?? '');
    const cc = this.emailFormGroup.get('ccList')?.value ?? [];
    const bcc = this.emailFormGroup.get('bccList')?.value ?? [];
    if (fieldName === 'receipientEmail') {
      return cc.includes(email) || bcc.includes(email);
    }
    if (fieldName === 'ccList') {
      return to === email || bcc.includes(email);
    }
    if (fieldName === 'bccList') {
      return to === email || cc.includes(email);
    }
    return false;
  }

  ngOnInit(): void {
    this.http.get('api/companyprofile/logo?_silent=true').subscribe({
      next: res => {
        if (res && res.companyImage !== null)
          this.companyLogo = this.normalizeImageUrl(res.companyImage);
        else
          this.companyLogo = this.defaultCompanyLogo;
      },
      error: () => {
        this.companyLogo = this.defaultCompanyLogo;
      }
    });
  }

  private normalizeImageUrl(url?: string): string {
    if (!url) return this.defaultCompanyLogo;
    return url.replace(/ /g, '%20');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reportTypes'] || changes['einvoiceControl']) {
      this.loadReportFormats();
    }
  }

  handle(event: any) {
    if (!event.type || !event.transactionId) return;

    this.sendOption = event.option;
    this.loading = true;
    this.http
      .post('api/transactionSender/info?_silent=true', {
        type: event.type,
        transactionId: event.transactionId,
        option: event.option
      })
      .subscribe((res: any) => {
        this.data = res;
        this.data.companyWebsite = '';
        this.data.primaryEmail = this.data.receipientEmail; // Set primary email
        // Setup CC functionality
        if (this.data.ccList && this.data.ccList.length > 0) {
          this.data.ccOptions = this.data.ccList.slice(); // Copy available options
          this.data.ccList = this.data.ccList.slice(); // Default: all selected
        } else {
          this.data.ccOptions = [];
          this.data.ccList = [];
        }
        // Setup BCC functionality - parse from string to array if needed
        if (this.data.bcc && typeof this.data.bcc === 'string') {
          this.data.bccList = this.data.bcc
            .split(',')
            .map((email: string) => email.trim())
            .filter((email: string) => email);
        } else if (this.data.bccList && Array.isArray(this.data.bccList)) {
          this.data.bccList = this.data.bccList.slice();
        } else {
          this.data.bccList = [];
        }
        // Initialize filtered email suggestions
        this.filteredEmailSuggestions = this.data.ccOptions
          ? [...this.data.ccOptions]
          : [];
        this.loading = false;
        if (this.sendOption === 'link') {
          this.generateLink();
        } else {
          if (this.sendOption) this.modalVisible[this.sendOption] = true;

          if (this.sendOption === 'whatsapp')
            this.data.receipientPhone = this.data.receipientPhones[0];
          
          // Trigger change detection for OnPush parent components
          this.cdr.detectChanges();
        }
      });
  }

  handleReport(event: any) {
    if (this.loading) {
      return;
    }
    this.sendOption = event;
    this.setReportShareLoading(true);
    this.http
      .post('api/transactionSender/reportInfo?_silent=true', {
        taskId: this.taskId,
        option: event
      })
      .subscribe({
        next: (res: any) => {
          this.data = res;
          this.data.companyWebsite = '';
          // reportInfo returns singular receipientEmail (ReportSenderDto.ReceipientEmail)
          this.data.primaryEmail = this.data.receipientEmail ?? '';
          // Setup CC functionality from report data
          if (this.data.ccList && this.data.ccList.length > 0) {
            this.data.ccOptions = this.data.ccList.slice(); // Copy available options
            this.data.ccList = this.data.ccList.slice(); // Default: all selected
          } else {
            this.data.ccOptions = [];
            this.data.ccList = [];
          }
          // Setup BCC functionality - parse from string to array if needed
          if (this.data.bcc && typeof this.data.bcc === 'string') {
            this.data.bccList = this.data.bcc
              .split(',')
              .map((email: string) => email.trim())
              .filter((email: string) => email);
          } else if (this.data.bccList && Array.isArray(this.data.bccList)) {
            this.data.bccList = this.data.bccList.slice();
          } else {
            this.data.bccList = [];
          }
          // Initialize filtered email suggestions for reports
          this.filteredEmailSuggestions = this.data.ccOptions
            ? [...this.data.ccOptions]
            : [];
          if (this.sendOption === 'link') {
            this.generateLink();
          } else {
            this.setReportShareLoading(false);
            if (
              this.sendOption === 'whatsapp' &&
              this.data.receipientPhones?.length
            ) {
              this.data.receipientPhone = this.data.receipientPhones[0];
            }

            if (this.sendOption) this.modalVisible[this.sendOption] = true;

            // Trigger change detection for OnPush parent components
            this.cdr.detectChanges();
          }
        },
        error: () => {
          this.setReportShareLoading(false);
          this.cdr.detectChanges();
        }
      });
  }

  private getSendOption() {
    switch (this.sendOption) {
      case 'email':
        return 1;
      case 'whatsapp':
        return 2;
      case 'telegram':
        return 3;
      default:
        return 4;
    }
  }

  handleEmailSubmission() {
    if (this.errors.message) {
      return;
    }

    this.emailFormSubmitted = true;
    this.emailFormGroup.markAllAsTouched();
    if (!this.emailFormGroup.valid) {
      this.cdr.markForCheck();
      return;
    }

    if (
      this.hasInvalidEmails('receipientEmail') ||
      this.hasInvalidEmails('ccList') ||
      this.hasInvalidEmails('bccList')
    ) {
      return;
    }

    this.syncEmailDataFromForm();

    if (this.showPreview) {
      this.sendEmail();
    } else {
      this.sendReport();
    }
  }

  sendReport() {
    const request = {
      // senderName: this.data.companyName,
      // senderEmail: this.data.email,
      receipientEmail: this.data.receipientEmail,
      subject: this.data.subject,
      message: this.data.message,
      taskId: this.taskId,
      cc: this.data.ccList.join(','),
      bcc: this.data.bccList.join(','),
      companyWebsite: this.data.companyWebsite || ''
    };

    this.modalVisible.email = false;
    this.closeModal('email');
    const notification = this.notificationSrv.create(
      'info',
      'Sending email...',
      'Please wait',
      { nzDuration: 0 }
    );

    this.http
      .post(
        'api/EmailSender/SendDocument',
        request
      )
      .subscribe({
        next: () => {
          this.notificationSrv.remove(notification.messageId);
        },
        error: () => {
          this.notificationSrv.remove(notification.messageId);
        }
      });
  }

  sendEmail() {
    var el = document.getElementById('email-preview');
    let request: any = {
      docCode: this.data.docCode,
      senderName: this.data.companyName,
      senderEmail: this.data.email,
      receipientEmail: this.data.receipientEmail,
      subject: this.data.subject,
      htmlContent: el?.innerHTML,
      message: this.data.message,
      type: this.getSendOption(),
      reportUrl: this.selectedReport,
      filter: JSON.stringify({ selection: [this.data.docCode] }),
      usePrimaryDb: true,
      cc: this.data.ccList.join(','),
      bcc: this.data.bccList.join(','),
      companyWebsite: this.data.companyWebsite || ''
    };

    if (this.docType()) {
      request = { ...request, docType: this.docType() };
    }

    this.modalVisible.email = false;
    this.closeModal('email');
    const notification = this.notificationSrv.create(
      'info',
      'Sending email...',
      'Please wait',
      { nzDuration: 0 }
    );

    this.http
      .post(
        'api/EmailSender/SendDocument',
        request
      )
      .subscribe({
        next: () => {
          this.notificationSrv.remove(notification.messageId);
        },
        error: () => {
          this.notificationSrv.remove(notification.messageId);
        }
      });
  }

  textChanged(field: string) {
    if (field === 'message') {
      if (this.modalVisible.email) {
        this.applyMessageLinkValidationFromEmailForm();
      } else {
        this.errors.message = !String(this.data.message ?? '').includes('[link]');
      }
    }

    if (field === 'receipientPhone') {
      this.errors.receipientPhone = !this.data.receipientPhone;
    }
  }

  generateLink() {
    const trackReportShareLoading = !this.showPreview;

    if (!this.sendOption) {
      if (trackReportShareLoading) {
        this.setReportShareLoading(false);
      }
      return;
    }
    if (this.errors.message || this.errors.receipientPhone) {
      if (trackReportShareLoading) {
        this.setReportShareLoading(false);
      }
      return;
    }

    if (trackReportShareLoading) {
      this.setReportShareLoading(true);
    }

    this.modalVisible[this.sendOption] = false;
    this.closeModal(this.sendOption);
    const notification = this.notificationSrv.create(
      'info',
      'Generating Link...',
      'Please wait',
      { nzDuration: 0 }
    );

    const request = {
      type: this.getSendOption(),
      phoneNo: this.data.receipientPhone,
      message: this.data.message,
      reportUrl: this.selectedReport,
      taskId: this.taskId,
      filter: JSON.stringify({ selection: [this.data.docCode] }),
      userPrimaryDb: true
    };

    this.http
      .post(
        `${this.reporting.reportServerMirrorUrl}/api/TransactionSender/GenerateLink?_silent=true`,
        request
      )
      .pipe(
        finalize(() => {
          if (trackReportShareLoading) {
            this.setReportShareLoading(false);
          }
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (res: any) => {
          this.notificationSrv.remove(notification.messageId);
          this.response = res;
          switch (this.sendOption) {
            case 'whatsapp':
            case 'telegram':
              window.open(res, '_blank');
              break;
            default:
              this.copyToClipboard(res);
              break;
          }
        },
        error: () => {
          this.notificationSrv.remove(notification.messageId);
        }
      });
  }

  copyToClipboard(data: any) {
    if (!data) return;

    if (this.cachedLink.messageId)
      this.notificationSrv.remove(this.cachedLink.messageId);

    navigator.clipboard
      .writeText(data)
      .then(() => {
        this.msg.success('Copied to clipboard');
      })
      .catch(() => {
        if (this.cachedLink.messageId)
          this.notificationSrv.remove(this.cachedLink.messageId);
        var notification = this.notificationSrv.create(
          'info',
          'Link Generated',
          this.linkModel,
          {
            nzDuration: 0
          }
        );

        this.cachedLink = {
          data: data,
          messageId: notification.messageId
        };
      });
  }

  copyLink() {
    this.copyToClipboard(this.cachedLink.data);
  }

  private loadReportFormats() {
    if (!this.showPreview) return;

    return new Promise((resolve, reject) => {
      if (this.selectedReport) true;
      if (!this.reportTypes) reject('No report types founds');

      const allowedReports = this.reportTypes.filter(x =>
        x.acl ? this.acl.can(x.acl) : this.acl.can(`${x.reportType}.view`)
      );

      if (!allowedReports?.length) reject('No report types founds');
      else {
        // Retrieve alll format type into array of string
        const formatTypes = allowedReports.map(x => x.reportType).join(',');

        // Reset report formats
        this.reportFormats = [];

        this.http
          .get(
            `${this.reporting.reportServerMirrorUrl}/api/ReportFiles/GetByType`,
            {
              reportType: formatTypes
            }
          )
          .subscribe((res: any[]) => {
            // reorder default report
            if (this.isAllowedReportType(formatTypes)) {
              const transactionType = this.getReportType(formatTypes);

              // Get the EInvDefault data for einvControl
              this.http
                .get('api/TenantData/EInvDefault')
                .subscribe((eInvData: any[]) => {
                  // Reorder report formats based on einvControl
                  const defaultReport = eInvData ? this.reorderReportFormats(
                    eInvData,
                    this.einvoiceControl,
                    transactionType
                  ) : null;

                  if (defaultReport) {
                    // Move defaultReport to the start of res
                    this.reportFormats = [
                      ...res.filter(r => r.reportUrl === defaultReport),
                      ...res.filter(r => r.reportUrl !== defaultReport)
                    ];
                    this.selectedReport = this.reportFormats[0].reportUrl;
                  }
                  else {
                    this.reportFormats = [...res];
                    this.selectedReport = this.reportFormats[0].reportUrl;
                  }
                });
            } else {
              // If not the stated report type
              this.reportFormats = res;
              this.selectedReport = res[0].reportUrl;
            }
          });
      }
    });
  }

  private reorderReportFormats(
    eInvData: any[],
    einvControl: number,
    transactionType: string
  ) {
    const einvControlMap: { [key: number]: string } = {
      0: 'draft',
      1: 'portal',
      2: 'full',
      3: 'exempted'
    };

    const formatKey = einvControlMap[einvControl]; // Get the format key based on einvControl

    if (transactionType && formatKey) {
      const transaction = eInvData.find(
        t => t.transactionType === transactionType
      );
      return transaction ? transaction[formatKey] : null; // Returns the full repx string or null if not found
    }
    return null;
  }

  getReportType(reportTypes: string) {
    switch (reportTypes) {
      case 'sinv-voucher':
        return 'SINV';
      case 'sdn-voucher':
        return 'SDN';
      case 'scn-voucher':
        return 'SCN';
      case 'cs-voucher':
        return 'CS';
      case 'pinv-voucher':
        return 'PINV';
      case 'pdn-voucher':
        return 'PDN';
      case 'pcn-voucher':
        return 'PCN';
      case 'cp-voucher':
        return 'CP';
      default:
        return '';
    }
  }

  private isAllowedReportType(reportType: string): boolean {
    // List of allowed report types
    const allowedReportTypes = [
      'sinv-voucher',
      'sdn-voucher',
      'scn-voucher',
      'cs-voucher',
      'pinv-voucher',
      'pdn-voucher',
      'pcn-voucher',
      'cp-voucher'
    ];

    // Check if reportType is in the allowed list
    return allowedReportTypes.includes(reportType);
  }

  closeModal(sendOption: string) {
    if (sendOption === 'email') {
      this.isBccVisible = false;
      this.syncEmailDataFromForm();
      this.emailFormSubmitted = false;
    }
    this.modalVisible[sendOption] = false;
    this.modalClosed.emit(sendOption);
  }
}
